import React, { useEffect, Suspense, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, useNavigate } from 'react-router-dom';
import { useAppContext, View } from './context/AppContext';
import { useAuth } from './context/AuthContext';
import './i18n'; // Initialize i18next
import { Navbar } from './components/common/Navbar';
import { Header } from './components/common/Header';
import { GroupInfo } from './components/common/GroupInfo';
import { AdBanner } from './components/ads/AdBanner';
import { SplashScreen } from './components/common/SplashScreen';
import { GlobalAnnouncement } from './components/common/GlobalAnnouncement';
import { MobileDrawer } from './components/common/MobileDrawer';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BannedScreen } from './components/common/BannedScreen';
import { OnboardingTour, DEFAULT_ONBOARDING_STEPS } from './components/ui/OnboardingTour';
import { NotificationPrompt } from './components/ui/NotificationPrompt';
import { CookieConsent } from './components/common/CookieConsent';
import { refreshTokenIfNeeded, onForegroundMessage } from './services/pushNotificationService';
import { DesktopSidebar } from './components/layout/DesktopSidebar';
import { InviteConfirmationModal } from './components/modals/InviteConfirmationModal';
import {
  validateAndUseInviteToken,
  validateInviteToken,
  InviteToken,
  acceptInvite
} from './utils/inviteHelpers';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';

const GlobalShareStoryModal = lazyWithRetry(() => import('./components/modals/GlobalShareStoryModal').then(module => ({ default: module.GlobalShareStoryModal })));

const Home = lazyWithRetry(() => import('./pages/Home').then(module => ({ default: module.Home })));
const GroupSelect = lazyWithRetry(() => import('./pages/GroupSelect').then(module => ({ default: module.GroupSelect })));

// Lazy load pages
const Tournaments = lazyWithRetry(() => import('./pages/Tournaments').then(module => ({ default: module.Tournaments })));
const TournamentDetail = lazyWithRetry(() => import('./pages/TournamentDetail').then(module => ({ default: module.TournamentDetail })));
const Players = lazyWithRetry(() => import('./pages/Players').then(module => ({ default: module.Players })));
const PlayerDetail = lazyWithRetry(() => import('./pages/PlayerDetail').then(module => ({ default: module.PlayerDetail })));
const Clubs = lazyWithRetry(() => import('./pages/Clubs').then(module => ({ default: module.Clubs })));
const HeadToHead = lazyWithRetry(() => import('./pages/HeadToHead').then(module => ({ default: module.HeadToHead })));
const Bracket = lazyWithRetry(() => import('./pages/Bracket').then(module => ({ default: module.Bracket })));
const CreatePlayer = lazyWithRetry(() => import('./pages/CreatePlayer').then(module => ({ default: module.CreatePlayer })));
const CreateTournament = lazyWithRetry(() => import('./pages/CreateTournament').then(module => ({ default: module.CreateTournament })));
const CreateTeam = lazyWithRetry(() => import('./pages/CreateTeam').then(module => ({ default: module.CreateTeam })));
const CreateMatch = lazyWithRetry(() => import('./pages/CreateMatch').then(module => ({ default: module.CreateMatch })));
const MatchmakingCreate = lazyWithRetry(() => import('./pages/MatchmakingCreate').then(module => ({ default: module.MatchmakingCreate })));
const KotcDetail = lazyWithRetry(() => import('./pages/KotcDetail').then(module => ({ default: module.KotcDetail })));
const Matchmaking = lazyWithRetry(() => import('./pages/Matchmaking').then(module => ({ default: module.Matchmaking })));
const Login = lazyWithRetry(() => import('./pages/Login').then(module => ({ default: module.Login })));
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding').then(module => ({ default: module.Onboarding })));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage').then(module => ({ default: module.ProfilePage })));
const Rules = lazyWithRetry(() => import('./pages/Rules').then(module => ({ default: module.Rules })));
const InboxPage = lazyWithRetry(() => import('./pages/InboxPage').then(module => ({ default: module.InboxPage })));
const ChallengePage = lazyWithRetry(() => import('./pages/ChallengePage').then(module => ({ default: module.ChallengePage })));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage').then(module => ({ default: module.DashboardPage })));
const HubPage = lazyWithRetry(() => import('./pages/HubPage').then(module => ({ default: module.HubPage })));
const HubMyTournamentsPage = lazyWithRetry(() => import('./pages/HubMyTournamentsPage').then(module => ({ default: module.HubMyTournamentsPage })));
const HubChallengePlayersPage = lazyWithRetry(() => import('./pages/HubChallengePlayersPage').then(module => ({ default: module.HubChallengePlayersPage })));
const FriendsPage = lazyWithRetry(() => import('./pages/FriendsPage').then(module => ({ default: module.FriendsPage })));
const GlobalMatchesPage = lazyWithRetry(() => import('./pages/GlobalMatchesPage').then(module => ({ default: module.GlobalMatchesPage })));
const GlobalTournamentsPage = lazyWithRetry(() => import('./pages/GlobalTournamentsPage').then(module => ({ default: module.GlobalTournamentsPage })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const CreateChallenge = lazyWithRetry(() => import('./pages/CreateChallenge').then(module => ({ default: module.CreateChallenge })));
const FullFeedPage = lazyWithRetry(() => import('./pages/FullFeedPage').then(module => ({ default: module.FullFeedPage })));
const EditGroupPage = lazyWithRetry(() => import('./pages/EditGroupPage').then(module => ({ default: module.EditGroupPage })));
const SubscriptionPage = lazyWithRetry(() => import('./pages/SubscriptionPage').then(module => ({ default: module.SubscriptionPage })));
const SubscriptionSuccessPage = lazyWithRetry(() => import('./pages/SubscriptionSuccessPage').then(module => ({ default: module.SubscriptionSuccessPage })));
const GroupAdmin = lazyWithRetry(() => import('./pages/GroupAdmin').then(module => ({ default: module.GroupAdmin })));
const LegalPage = lazyWithRetry(() => import('./pages/LegalPage').then(module => ({ default: module.LegalPage })));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const LiveTVPage = lazyWithRetry(() => import('./pages/LiveTVPage').then(module => ({ default: module.LiveTVPage })));
const GroupChatPage = lazyWithRetry(() => import('./pages/GroupChatPage').then(module => ({ default: module.GroupChatPage })));

import { JoinGroupGate } from './components/group/JoinGroupGate';

const GroupWrapper = () => {
  const { groupId } = useParams();
  const { setCurrentGroup, db } = useAppContext();
  const { user } = useAuth();
  const [isMember, setIsMember] = useState<boolean | null>(null);

  useEffect(() => {
    if (groupId) {
      if (db.groups.length > 0) {
        // Try finding by ID, or by matching slug, or by matching normalized name
        const normalizedParam = groupId.toLowerCase();
        const group = db.groups.find(g => 
          g.id === groupId || 
          (g.slug && g.slug.toLowerCase() === normalizedParam) || 
          g.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === normalizedParam
        );
        
        if (group) {
          setCurrentGroup(group.id);
          // TRULY verify membership because AppContext temporarily injects public/visited groups into db.groups
          let trulyMember = false;
          if (user) {
            trulyMember = 
              group.ownerId === user.uid ||
              (group.admins && group.admins.includes(user.uid)) ||
              (group.members && group.members.includes(user.uid)) ||
              (group.players && group.players.some((p: any) => p.id === user.uid));
          }
          setIsMember(trulyMember);
        } else {
          setIsMember(false);
        }
      } else if (db.groups.length === 0) {
          setIsMember(false);
      }
    }
  }, [groupId, db.groups, user, setCurrentGroup]);

  // While checking membership, or if we determined they are NOT a member
  if (isMember === false && groupId) {
    return <JoinGroupGate slugOrId={groupId} />;
  }

  // Still checking or is member
  return <Outlet />;
};

import { PadelScoreCounter } from './components/match/PadelScoreCounter';

import { ErrorBoundary } from './components/common/ErrorBoundary';
import { OfflineFallback } from './components/common/OfflineFallback';

// Lightweight skeleton for the main content area (shown during lazy chunk loading)
const PageSkeleton = () => (
  <div className="p-4 space-y-6">
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-surface-2/60 rounded-lg w-3/4"></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="h-20 bg-surface-2/60 rounded-xl"></div>
        <div className="h-20 bg-surface-2/60 rounded-xl"></div>
      </div>
      <div className="h-48 bg-surface-2/60 rounded-xl"></div>
    </div>
  </div>
);

// MainLayout defined OUTSIDE App to maintain stable component identity.
const MainLayout: React.FC<{ children: React.ReactNode, isPublic?: boolean }> = ({ children, isPublic }) => {
  const { view, currentGroup } = useAppContext();
  const { user } = useAuth();

  const showGroupInfo = view !== View.GROUP_SELECT && view !== View.PROFILE && view !== View.CREATE_PLAYER && view !== View.RULES && view !== View.INBOX && view !== View.CHALLENGES && view !== View.DASHBOARD && view !== View.CLUBS && view !== View.FRIENDS;
  const showNavbar = !!currentGroup && showGroupInfo;

  const isChat = view === View.GROUP_CHAT;

  return (
    <div className={`flex flex-col lg:flex-row ${isChat ? 'fixed inset-0 overflow-hidden overscroll-behavior-none' : 'min-h-[100dvh] bg-background'}`}>
      <MobileDrawer />
      {!isPublic && user && <DesktopSidebar />}
      <div className={`flex-1 min-w-0 flex flex-col ${isChat ? 'h-full overflow-hidden' : 'relative'}`}>
        <div
          className={`w-full max-w-lg lg:max-w-4xl mx-auto relative min-w-0 flex flex-col ${isChat ? 'h-full overflow-hidden' : 'flex-1 pb-24 lg:pb-0'}`}
        >
          <div className="flex-shrink-0 z-20">
            {!isChat && <Header />}
            {!isPublic && !isChat && <GlobalAnnouncement />}
            {!isPublic && !isChat && <AdBanner />}
            {!isPublic && !isChat && showGroupInfo && <GroupInfo />}
          </div>

          <main className={`flex-1 min-h-0 flex flex-col ${isChat ? 'h-full overflow-hidden' : 'overflow-x-hidden'}`}>
            <Suspense fallback={<PageSkeleton />}>
              {children}
            </Suspense>
          </main>

          <div className="flex-shrink-0 z-50">
            {!isPublic && showNavbar && <Navbar />}
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Version v1.4.0
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);


  const { showToast } = useAppContext();
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [inviteProcessing, setInviteProcessing] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState<InviteToken | null>(null);

  // App initialization stabilization gate
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    if (!loading && user !== undefined && userData !== undefined) {
      // Small delay to allow AppContext to sync local & remote db state
      const timer = setTimeout(() => setIsAppReady(true), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, user, userData]);

  // --- FCM: Refresh token on every login & listen for foreground messages ---
  useEffect(() => {
    if (!isAppReady || !user) return;

    // 1. Silently refresh FCM token (prevents stale tokens)
    refreshTokenIfNeeded(user.uid);

    // 2. Listen for foreground messages and display in-app toast
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'Padel Brothers';
      const body = payload.notification?.body || '';
      showToast(`${title}: ${body}`, 'info');
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isAppReady, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep Linking Logic for Invite Tokens
  // Capture Invite Token on Mount (Persist through login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    if (inviteToken) {
      localStorage.setItem('pending_invite_token', inviteToken);
    }
  }, []);

  // Track if we've already processed the invite to prevent re-runs
  const inviteCheckedRef = React.useRef(false);

  // Process Invite Token (from URL or LocalStorage)
  useEffect(() => {
    // Single check - only run once when user + userData are both ready AND app is stabilized
    if (!isAppReady || inviteCheckedRef.current || !user || !userData || inviteProcessing || inviteModalOpen) {
      return;
    }

    const checkInvite = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlInviteToken = params.get('invite');
      const storedInviteToken = localStorage.getItem('pending_invite_token');

      const tokenString = urlInviteToken || storedInviteToken;

      /* console.log('[App] Checking Invite:', {
        urlToken: urlInviteToken,
        storedToken: storedInviteToken,
        finalToken: tokenString,
        userCb: !!user,
        userDataCb: !!userData
      }); */

      // Only proceed if we actually have a token to check
      if (!tokenString) {
        // console.log('[App] No token found. Marking checked.');
        inviteCheckedRef.current = true;
        return;
      }

      // Mark as checked before processing to prevent re-entry
      inviteCheckedRef.current = true;
      setInviteProcessing(true);

      try {
        // console.log('[App] Validating token...', tokenString);

        const validToken = await validateInviteToken(tokenString);
        // console.log('[App] Token validation result:', validToken);

        if (validToken) {
          // PROGRESSIVE DISCLOSURE: Special handling for tournament invites
          if (validToken.type === 'tournament' && validToken.groupId && validToken.tournamentId) {
            const { db } = await import('./firebaseConfig');
            
            // 1. Check if user is already in the group
            // Use getDocFromServer to bypass Firestore cache — after JoinGroupGate's
            // window.location.reload(), the cache may not yet reflect the arrayUnion write
            let groupDoc;
            try {
              groupDoc = await getDocFromServer(doc(db, 'groups', validToken.groupId));
            } catch {
              // Fallback to cache if offline or server unreachable
              groupDoc = await getDoc(doc(db, 'groups', validToken.groupId));
            }
            const groupData = groupDoc.exists() ? groupDoc.data() : null;
            const isGroupMember = groupData && (
              groupData.members?.includes(user!.uid) || 
              (groupData.players && groupData.players.some((p: any) => p.id === user!.uid))
            );

            if (!isGroupMember) {
              // Not in group -> Redirect to group to trigger JoinGroupGate
              console.log('[App] User not in group. Redirecting to JoinGroupGate...', validToken.groupId);
              setInviteProcessing(false);
              // KEEP the invite token in both URL and localStorage so it gets
              // reprocessed after JoinGroupGate's window.location.reload()
              // Do NOT delete the token from URL or localStorage here.
              navigate(`/group/${validToken.groupId}${urlInviteToken ? `?invite=${urlInviteToken}` : ''}`);
              return; 
            }

            // 2. User IS in the group. Check if already in the tournament.
            let tournDoc;
            try {
              tournDoc = await getDocFromServer(doc(db, 'groups', validToken.groupId, 'tournaments', validToken.tournamentId));
            } catch {
              tournDoc = await getDoc(doc(db, 'groups', validToken.groupId, 'tournaments', validToken.tournamentId));
            }
            const tournData = tournDoc.exists() ? tournDoc.data() : null;
            
            let isTournMember = false;
            if (tournData) {
              if (tournData.format?.toLowerCase().includes('americano') || tournData.format?.toLowerCase().includes('mexicano')) {
                isTournMember = tournData.config?.americanoPlayerIds?.includes(user!.uid) || false;
              } else {
                isTournMember = tournData.participants?.includes(user!.uid) || 
                                tournData.teams?.some((t: any) => t.player1Id === user!.uid || t.player2Id === user!.uid) || false;
              }
            }

            if (isTournMember) {
              // Already in tournament! Skip modal and navigate to dashboard directly
              console.log('[App] User already in tournament. Redirecting to dashboard...');
              localStorage.removeItem('pending_invite_token');
              if (urlInviteToken) {
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('invite');
                window.history.replaceState({}, '', newUrl.toString());
              }
              setInviteProcessing(false);
              navigate(`/group/${validToken.groupId}/tournament/${validToken.tournamentId}`);
              return;
            }
            
            // If they are in the group but NOT in the tournament, we fall back to the modal below
          }

          // console.log('[App] Token valid! Opening modal.');
          // Remove from URL to clean up history, but keep state
          if (urlInviteToken) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('invite');
            window.history.replaceState({}, '', newUrl.toString());
          }

          setPendingInviteData(validToken);
          setInviteModalOpen(true);
        } else {
          console.warn('[App] Token invalid or expired.');
          showToast("Lien d'invitation invalide ou expiré.", 'error');
          localStorage.removeItem('pending_invite_token');

          if (urlInviteToken) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('invite');
            window.history.replaceState({}, '', newUrl.toString());
          }
        }
      } catch (error) {
        console.error('[App] Error checking invite:', error);
      } finally {
        setInviteProcessing(false);
      }
    };

    checkInvite();
  }, [isAppReady, user, userData, inviteProcessing, inviteModalOpen, showToast, navigate]);

  const handleConfirmJoin = async () => {
    if (!pendingInviteData || !user || !userData) return;

    setInviteProcessing(true);
    try {
      const token = await validateAndUseInviteToken(pendingInviteData.id, user.uid);

      if (token) {
        const result = await acceptInvite(
          user.uid,
          userData,
          token.type,
          token.groupId,
          token.tournamentId
        );

        if (result.success) {
          showToast(result.message, 'success');

          console.log('[Invite] Navigation debug:', {
            tokenType: token.type,
            groupId: token.groupId,
            tournamentId: token.tournamentId,
            shouldGoToTournament: token.type === 'tournament' && token.tournamentId
          });

          // Wait for Firestore real-time listeners to pick up the write
          // This prevents the "inscrit but not visible" issue
          await new Promise(resolve => setTimeout(resolve, 1500));

          // CRITICAL: Clean up invite state BEFORE triggering navigation.
          // If we let the `finally` block run setState calls after window.location.href,
          // React re-renders happen before the browser navigates, causing the app
          // to briefly settle on the default "/" route instead of the target URL.
          localStorage.removeItem('pending_invite_token');

          if (token.type === 'tournament' && token.tournamentId) {
            // Set Grace Period Marker for TournamentDetail access
            localStorage.setItem(`padel_recently_joined_${token.tournamentId}`, Date.now().toString());

            const targetUrl = `/group/${token.groupId}/tournament/${token.tournamentId}`;
            console.log('[Invite] Navigating to tournament:', targetUrl);
            // Use full page reload to ensure all Firestore listeners restart with fresh data
            window.location.href = targetUrl;
            return; // Skip finally block — page is reloading, no need to update React state
          } else {
            const targetUrl = `/group/${token.groupId}`;
            console.log('[Invite] Navigating to group:', targetUrl);
            window.location.href = targetUrl;
            return; // Skip finally block — page is reloading, no need to update React state
          }
        } else {
          showToast(result.message, 'error');
        }
      } else {
        showToast("Lien d'invitation expiré.", 'error');
      }
    } catch (error) {
      console.error('Error joining via invite:', error);
      showToast("Erreur lors de l'adhésion.", 'error');
    } finally {
      setInviteProcessing(false);
      setInviteModalOpen(false);
      setPendingInviteData(null);
      localStorage.removeItem('pending_invite_token');
    }
  };

  const handleDeclineJoin = () => {
    setInviteModalOpen(false);
    setPendingInviteData(null);
    localStorage.removeItem('pending_invite_token');
  };

  if (loading) {
    return <SplashScreen />;
  }

  // Before the application is fully ready, if we have a user and we're stabilizing, show splash
  // This prevents the UI (like GroupWrapper) from rendering empty state and showing JoinGroupGate prematurely
  if (user && !isAppReady) {
      return <SplashScreen />;
  }

  if (userData && (userData as any).isBanned) {
    return (
      <BannedScreen userData={userData} />
    );
  }

  return (
    <ErrorBoundary>
      {!isOnline && (
        <div className="bg-red-500 text-white text-center py-1 px-4 text-xs font-bold w-full z-50 fixed top-0 left-0 safe-area-pt">
          Vous êtes hors-ligne. Les données peuvent ne pas être à jour.
        </div>
      )}

      <>
        <Routes>
          {/* 1. PUBLIC ROUTES (Always Accessible with Header/Sidebar) */}
          <Route path="/legal" element={<MainLayout><LegalPage /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
          <Route path="/score" element={<MainLayout isPublic><PadelScoreCounter /></MainLayout>} />
          <Route path="/offline" element={<OfflineFallback />} />

          {/* 2. PROTECTED ROUTES & LOGIN FLOW */}
          <Route path="*" element={
            !user ? (
              <Suspense fallback={<SplashScreen />}>
                <div className="relative">
                  <Login />
                  <div className="fixed bottom-2 left-2 text-[10px] text-text-secondary opacity-30 select-none pointer-events-none">
                    v{__APP_VERSION__}
                  </div>
                </div>
              </Suspense>
            ) : !userData ? (
              <Suspense fallback={<SplashScreen />}>
                <Onboarding />
              </Suspense>
            ) : (
              <MainLayout>
                <Routes>
                  <Route path="/" element={<HubPage />} />
                  <Route path="/groups" element={<GroupSelect />} />
                  <Route path="/profile/:userId?" element={<ProfilePage />} />
                  <Route path="/profile/edit" element={<CreatePlayer />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/rules" element={<Rules />} />

                  <Route path="/group/:groupId" element={<GroupWrapper />}>
                    <Route index element={<Home />} />
                    <Route path="tournaments" element={<Tournaments />} />
                    <Route path="tournament/:tournamentId" element={<TournamentDetail />} />
                    <Route path="players" element={<Players />} />
                    <Route path="player/:playerId" element={<PlayerDetail />} />

                    <Route path="h2h" element={<HeadToHead />} />
                    <Route path="tournament/:tournamentId/bracket" element={<Bracket />} />
                    <Route path="create-player" element={<CreatePlayer />} />
                    <Route path="create-tournament" element={<CreateTournament />} />
                    <Route path="create-team" element={<CreateTeam />} />
                    <Route path="create-match" element={<CreateMatch />} />
                    <Route path="create-matchmaking" element={<MatchmakingCreate />} />
                    <Route path="matchmaking" element={<Matchmaking />} />
                    <Route path="kotc/:kotcId" element={<KotcDetail />} />
                    <Route path="feed" element={<FullFeedPage />} />
                    <Route path="edit" element={<EditGroupPage />} />
                    <Route path="tournament/:tournamentId/tv" element={<LiveTVPage />} />

                    <Route path="admin" element={<GroupAdmin />} />
                    <Route path="chat" element={<GroupChatPage />} />
                    <Route path="create-challenge" element={<CreateChallenge />} />
                  </Route>

                  <Route path="/challenges" element={<ChallengePage />} />
                  <Route path="/friends" element={<FriendsPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/hub/my-tournaments" element={<HubMyTournamentsPage />} />
                  <Route path="/hub/challenge-players" element={<HubChallengePlayersPage />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/clubs" element={<Clubs />} />
                  <Route path="/matches" element={<GlobalMatchesPage filter="all" />} />
                  <Route path="/my-tournaments" element={<GlobalTournamentsPage filter="all" />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/create-challenge" element={<CreateChallenge />} />
                  <Route path="/subscription" element={<SubscriptionPage />} />
                  <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/contact" element={<ContactPage />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                <OnboardingTour steps={DEFAULT_ONBOARDING_STEPS} onComplete={() => { }} />
                <NotificationPrompt />
                <CookieConsent onAccept={() => { }} onDecline={() => { }} />
                <div id="toast-container" className="fixed bottom-24 lg:bottom-4 right-4 w-full max-w-xs space-y-2 z-50 pointer-events-none"></div>

                {/* Invite Confirmation Modal */}
                <InviteConfirmationModal
                  isOpen={inviteModalOpen}
                  onClose={handleDeclineJoin}
                  onConfirm={handleConfirmJoin}
                  inviteData={pendingInviteData}
                  isProcessing={inviteProcessing}
                />
              </MainLayout>
            )
          } />
        </Routes>
      </>

      {/* Global Share Story Modal */}
      <Suspense fallback={null}>
        <GlobalShareStoryModal />
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
